/**
 * Red pixel-art "you've arrived!" callout shown next to Koko at the
 * moment a `[POINT:...]` flight resolves. Mirrors the navigation
 * pointer bubble in `OverlayWindow.swift:345-380`:
 *
 *   - Bright red fill with a white pixel border (DS.Colors.overlayCursorRed)
 *   - Anchored to the bird at 60 fps so it follows the sprite
 *   - Spring scale-in on show, opacity fade on hide
 *   - Holds the pointing label (e.g. "click the new task button")
 *
 * Distinct from `ResponseBubble` (dark, full-text, response stream) and
 * `FeedbackPill` (transient pixel pill for status flashes). This one is
 * a single-purpose surface for "look here!" moments.
 */

// Park the callout *above* the bird's head, not beside it. The
// response bubble / text panel already occupies the side slot, and
// stacking them there overlaps. Putting the callout above also reads
// like a comic-strip speech bubble pointing down at what the bird is
// showing — closer to the native "look here!" affordance.
const CALLOUT_GAP_ABOVE_BIRD_CENTER_PX = 26 + 12;
const POSITION_TICK_MS = 1000 / 60;

export interface PointingCalloutBubbleOptions {
  getBirdCenter: () => { x: number; y: number };
}

export class PointingCalloutBubble {
  private readonly bubbleElement: HTMLDivElement;
  private readonly contentElement: HTMLSpanElement;
  private readonly options: PointingCalloutBubbleOptions;
  private positionTimerId: number | null = null;

  constructor(options: PointingCalloutBubbleOptions) {
    this.options = options;
    this.bubbleElement = document.createElement("div");
    this.bubbleElement.className = "koko-pixel-box koko-pointing-callout";

    const fillLayer = document.createElement("div");
    fillLayer.className = "koko-pixel-box-fill";

    this.contentElement = document.createElement("span");
    this.contentElement.className = "koko-content";

    fillLayer.appendChild(this.contentElement);
    this.bubbleElement.appendChild(fillLayer);
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.bubbleElement);
  }

  /**
   * Pop the callout next to the bird with the given label. Idempotent —
   * calling `show` a second time replaces the label and re-runs the
   * scale-in animation.
   */
  show(label: string): void {
    this.contentElement.textContent = label;
    this.positionToBird();
    // Force a reflow so the next class change retriggers the transition.
    // Without this, calling `show()` while still visible doesn't replay
    // the scale-bounce — the browser elides the no-op class toggle.
    this.bubbleElement.classList.remove("visible");
    void this.bubbleElement.offsetHeight;
    this.bubbleElement.classList.add("visible");
    this.startPositionTimer();
  }

  hide(): void {
    this.bubbleElement.classList.remove("visible");
    this.stopPositionTimer();
  }

  destroy(): void {
    this.hide();
    this.bubbleElement.remove();
  }

  private startPositionTimer(): void {
    if (this.positionTimerId !== null) return;
    this.positionTimerId = window.setInterval(
      () => this.positionToBird(),
      POSITION_TICK_MS,
    );
  }

  private stopPositionTimer(): void {
    if (this.positionTimerId !== null) {
      window.clearInterval(this.positionTimerId);
      this.positionTimerId = null;
    }
  }

  private positionToBird(): void {
    const birdCenter = this.options.getBirdCenter();
    const bubbleWidth = this.bubbleElement.offsetWidth || 240;
    const bubbleHeight = this.bubbleElement.offsetHeight || 44;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Default: park above the bird, horizontally centered on its center.
    // If the bird is too close to the top to fit, flip below the sprite
    // so the callout never gets clipped.
    let topEdge = birdCenter.y - CALLOUT_GAP_ABOVE_BIRD_CENTER_PX - bubbleHeight;
    let transformOrigin = "center bottom";
    if (topEdge < 8) {
      topEdge = birdCenter.y + CALLOUT_GAP_ABOVE_BIRD_CENTER_PX;
      transformOrigin = "center top";
    }
    topEdge = Math.max(8, Math.min(viewportHeight - bubbleHeight - 8, topEdge));

    let leftEdge = birdCenter.x - bubbleWidth / 2;
    leftEdge = Math.max(8, Math.min(viewportWidth - bubbleWidth - 8, leftEdge));

    this.bubbleElement.style.transformOrigin = transformOrigin;
    this.bubbleElement.style.left = `${leftEdge}px`;
    this.bubbleElement.style.top = `${topEdge}px`;
  }
}
